import React, { useState, useEffect } from 'react';
import ObjectPhotosSlider from '~components/ObjectPhotosSlider';
import ImageGallery from '~components/ImageGallery';
import useGallery from '~components/ImageGallery/hooks/useGallery';
import FeaturesList from '~components/FeaturesList';
import { Label } from '~components/FeaturesList/components/Feature/styledComponents';
import { getPhotos } from '~src/api/get';
import { NUMBER_TO_BOOL_MAP } from '~constants/index';
import FavoriteButton from '~components/FavoriteButton';
import { ViewTo, ObjectType, BrokerListingObject } from '~entities/Object';
import {
  Block,
  SliderBlock,
  DetailsBlock,
  AddToFavorites,
  Title,
  Address,
  Container,
  OfficeName,
  Taxes,
  StyledFeatureValue,
  Description,
} from './styledComponents';

const objectTypesMap: Record<ObjectType['type'], string> = {
  apartment: 'апартаментов',
  torg: 'торговых помещений',
  office: 'офисов',
};

export type Props = {
  data: {
    id_crm: ObjectType['id_crm'];
    url: ObjectType['url'];
    name: ObjectType['name'];
    type_listing: ObjectType['type_listing'];
    address: ObjectType['address'];
    furnish: ObjectType['furnish'];
    furniture: ObjectType['furniture'];
    ceil: ObjectType['ceil'];
    view_to: ObjectType['view_to'];
    type: ObjectType['type'];
    tax?: BrokerListingObject['tax'];
    desc?: BrokerListingObject['desc'];
    rooms?: BrokerListingObject['rooms'];
  };
  footerElem?: React.ReactNode;
  showBedrooms?: boolean;
  desktopSmallView?: boolean;
  favorites?: { toggleFavorite: () => void; isInFavorites: boolean };
};

// shows rooms in featureList if data contains rooms
const ObjectDetailsWithGallery = ({
  desktopSmallView,
  footerElem,
  favorites,
  data: {
    id_crm,
    name,
    type_listing,
    address,
    furnish,
    furniture,
    ceil,
    view_to,
    type,
    tax,
    desc,
    rooms,
  },
}: Props) => {
  const [photos, setPhotos] = useState<{
    previews: string[];
    images: string[];
    plans: string[];
  }>({
    previews: [],
    images: [],
    plans: [],
  });
  const {
    isGalleryOpen,
    galleryStartIndex,
    closeGallery,
    openGallery,
  } = useGallery(0);

  const { images, plans } = photos;

  const imagesStartIndex = plans.length;
  const plansStartIndex = plans.length ? imagesStartIndex - 1 : undefined;

  const photoSrcs = [...plans, ...images];

  const viewTo = view_to.map((key, i, arr) => {
    const value = ViewTo[key];
    const isLast = i === arr.length - 1;

    return (
      <div key={value}>
        {value}
        {!isLast && ','}
      </div>
    );
  });

  const secondFeature = rooms
    ? { label: 'Спален', value: rooms }
    : {
        label: 'Мебель',
        value: NUMBER_TO_BOOL_MAP[furniture] ? 'Ecть' : 'Нет',
      };

  const features = [
    { label: 'Отделка', value: furnish },
    secondFeature,
    { label: 'Вид на', value: viewTo },
    { label: 'Потолки', value: `${ceil} м` },
  ];

  useEffect(() => {
    getPhotos({ query: { idd: id_crm } }).then(({ data }) => {
      // plans key isn't sent if there is no plans
      setPhotos({ ...data, plans: data.plans || [] });
    });
  }, []);

  return (
    <React.Fragment>
      <Block>
        <Container desktopSmallView={desktopSmallView}>
          <SliderBlock desktopSmallView={desktopSmallView}>
            <ObjectPhotosSlider
              id_crm={id_crm}
              desktopSmallView={desktopSmallView}
              srcs={photoSrcs}
              onSlideClick={(i) => openGallery(i)}
              plansStartIndex={plansStartIndex}
              imagesStartIndex={imagesStartIndex}
              favorites={favorites}
            />
          </SliderBlock>
          <DetailsBlock>
            {favorites && (
              <AddToFavorites onClick={favorites.toggleFavorite}>
                Добавить в избраное{' '}
                <FavoriteButton
                  isActiveGrey
                  isActive={favorites.isInFavorites}
                  size={16}
                />
              </AddToFavorites>
            )}
            <Title>
              {type_listing === 'rent' ? 'Аренда' : 'Продажа'}{' '}
              {objectTypesMap[type]} в <OfficeName>{name}</OfficeName>
            </Title>
            <Address>Москва, {address}</Address>
            <FeaturesList
              desktopSmallView={desktopSmallView}
              features={features}
            />
            {tax && (
              <Taxes>
                <Label>Налогообложение:</Label>
                <StyledFeatureValue>&nbsp;{tax}</StyledFeatureValue>
              </Taxes>
            )}
            {desc && <Description>{desc}</Description>}
            {footerElem}
          </DetailsBlock>
        </Container>
      </Block>
      <ImageGallery
        startIndex={galleryStartIndex}
        srcs={photoSrcs}
        isOpen={isGalleryOpen}
        onClose={closeGallery}
      />
    </React.Fragment>
  );
};

export default ObjectDetailsWithGallery;
